import React, { useEffect, useState } from "react";
import type { CategoryNode } from "@/types/pim";
import { Select } from "antd";
import { useInventoryFilters } from "./InventoryFiltersContext";

interface AttributeOption { value: any; count: number; disabled?: boolean; code?: string };

const AttributeFilters: React.FC<{
  category: CategoryNode | null;
  filters: Record<string, any>;
  searchQuery?: string;
  onFilterChange: (attributeName: string, value: any) => void;
}> = ({ filters, onFilterChange }) => {
  // Server-computed datasets (definitions, distinct values, availability + counts),
  // provided via context — no client fetch.
  const { attributes, attributeValues, availableValues } = useInventoryFilters();

  // Optimistic local state - immediately reflects user selections
  const [optimisticFilters, setOptimisticFilters] = useState<Record<string, any>>(filters);

  // Sync optimistic state with actual filters when they arrive
  useEffect(() => {
    setOptimisticFilters(filters);
  }, [filters]);

  // Wrapper for onFilterChange that updates optimistically first
  const handleFilterChange = (attributeName: string, value: any) => {
    // Immediately update local state for responsive UI
    setOptimisticFilters(prev => {
      if (value === null || value === undefined) {
        const { [attributeName]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [attributeName]: value };
    });

    // Then trigger the actual filter change (updates search params)
    onFilterChange(attributeName, value);
  };

  // Use optimistic filters for display
  const displayFilters = optimisticFilters;

  if (attributes.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic px-3 py-2">
        No specific attributes available for this category.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {attributes.map((attr) => {
        // Location is surfaced as a dedicated full-width control above the grid
        // (see InventoryList.LocationFilter), so hide it here.
        if (attr.AttributeName === "Location") return null;

        const available = availableValues[attr.AttributeName] || [];
        const isAttributeDisabled = available.length === 0 || (available.length === 1 && available[0].count === 0);

        return (
          <div
            key={attr.AttributeName}
            className={`px-3 ${isAttributeDisabled ? 'opacity-50' : ''}`}
          >
            {(attr.AttributeValueType !== 0 ||
              (attr.AttributeValueType === 0 && attributeValues[attr.AttributeName]?.length > 0)) && (
                <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                  {attr.AttributeName.replace(/([A-Z])/g, ' $1').trim()}
                </label>
              )}

            {/* String type - use displayFilters and handleFilterChange */}
            {attr.AttributeValueType === 0 && attributeValues[attr.AttributeName]?.length > 0 && (
              <Select
                mode="multiple"
                className="w-full"
                placeholder="Select options"
                value={
                  Array.isArray(displayFilters[attr.AttributeName])
                    ? displayFilters[attr.AttributeName]
                    : displayFilters[attr.AttributeName]
                      ? [displayFilters[attr.AttributeName]]
                      : []
                }
                onChange={(values: string[]) =>
                  handleFilterChange(attr.AttributeName, values.length > 0 ? values : null)
                }
                maxTagCount="responsive"
                disabled={isAttributeDisabled}
                options={attr.AttributeName === "Location"
                  ? available
                    .map((val: AttributeOption) => ({
                      label: `${val.value} (${val.count || 0})`,
                      value: val.code,
                      disabled: false,
                    }))
                    .sort((a, b) => String(a.label).localeCompare(String(b.label)))
                  : attributeValues[attr.AttributeName]
                    .map((val: any) => {
                      const availableOption = available.find((a) => a.value === val);
                      const count = availableOption?.count || 0;
                      const disabled = !availableOption || count === 0;
                      return {
                        label: `${val} ${count > 0 ? `(${count})` : '(0)'}`,
                        value: val,
                        disabled,
                      };
                    })
                    // Available options first, then the disabled (no-items) ones;
                    // alphabetical within each group.
                    .sort((a, b) => {
                      if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
                      return String(a.value).localeCompare(String(b.value));
                    })
                }
                tagRender={(props) => {
                  const { label, value, closable, onClose } = props;
                  const displayLabel = typeof label === 'string' ? label.split(' (')[0] : label;

                  return (
                    <span className="ant-select-selection-item" style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <span className="ant-select-selection-item-content">{displayLabel}</span>
                      {closable && (
                        <span
                          className="ant-select-selection-item-remove"
                          onClick={onClose}
                          style={{ marginLeft: '4px', cursor: 'pointer' }}
                        >
                          ×
                        </span>
                      )}
                    </span>
                  );
                }}
              />
            )}

            {/* Number type - use displayFilters and handleFilterChange */}
            {attr.AttributeValueType === 1 && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    type="number"
                    placeholder="Min"
                    className="flex-1 min-w-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gcopper text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    value={displayFilters[attr.AttributeName]?.min || ''}
                    onChange={(e) =>
                      handleFilterChange(attr.AttributeName, {
                        ...displayFilters[attr.AttributeName],
                        min: e.target.value ? parseFloat(e.target.value) : null,
                      })
                    }
                    disabled={isAttributeDisabled}
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="flex-1 min-w-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gcopper text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    value={displayFilters[attr.AttributeName]?.max || ''}
                    onChange={(e) =>
                      handleFilterChange(attr.AttributeName, {
                        ...displayFilters[attr.AttributeName],
                        max: e.target.value ? parseFloat(e.target.value) : null,
                      })
                    }
                    disabled={isAttributeDisabled}
                  />
                </div>
              </div>
            )}

            {/* Boolean type - use displayFilters and handleFilterChange */}
            {attr.AttributeValueType === 2 && (
              <div className="space-y-1">
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`flex-1 min-w-15 px-3 py-2 rounded-md text-sm font-medium transition-colors ${displayFilters[attr.AttributeName] === null ||
                      displayFilters[attr.AttributeName] === undefined
                      ? 'bg-gray-200 text-gray-700'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                      } ${isAttributeDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => !isAttributeDisabled && handleFilterChange(attr.AttributeName, null)}
                    disabled={isAttributeDisabled}
                  >
                    All
                  </button>
                  {[true, false].map((boolValue) => {
                    const availableOption = available.find((a) => a.value === boolValue);
                    const count = availableOption?.count || 0;
                    const disabled = !availableOption || count === 0;

                    return (
                      <button
                        key={String(boolValue)}
                        className={`flex-1 min-w-15 px-3 py-2 rounded-md text-sm font-medium transition-colors ${displayFilters[attr.AttributeName] === boolValue
                          ? 'bg-gcopper text-white'
                          : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !disabled && handleFilterChange(attr.AttributeName, boolValue)}
                        disabled={disabled}
                      >
                        {boolValue ? 'Yes' : 'No'}
                        {count > 0 && ` (${count})`}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Date type - use displayFilters and handleFilterChange */}
            {attr.AttributeValueType === 3 && (
              <div className="space-y-2">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gcopper text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={displayFilters[attr.AttributeName]?.type || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      attr.AttributeName,
                      e.target.value
                        ? { type: e.target.value, startDate: '', endDate: '' }
                        : null
                    )
                  }
                  disabled={isAttributeDisabled}
                >
                  <option value="">No filter</option>
                  <option value="before">Before date</option>
                  <option value="after">After date</option>
                  <option value="between">Between dates</option>
                </select>

                {displayFilters[attr.AttributeName]?.type === 'before' && (
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gcopper text-sm disabled:bg-gray-100"
                    value={displayFilters[attr.AttributeName]?.startDate || ''}
                    onChange={(e) =>
                      handleFilterChange(attr.AttributeName, {
                        ...displayFilters[attr.AttributeName],
                        startDate: e.target.value,
                      })
                    }
                    disabled={isAttributeDisabled}
                  />
                )}

                {displayFilters[attr.AttributeName]?.type === 'after' && (
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gcopper text-sm disabled:bg-gray-100"
                    value={displayFilters[attr.AttributeName]?.startDate || ''}
                    onChange={(e) =>
                      handleFilterChange(attr.AttributeName, {
                        ...displayFilters[attr.AttributeName],
                        startDate: e.target.value,
                      })
                    }
                    disabled={isAttributeDisabled}
                  />
                )}

                {displayFilters[attr.AttributeName]?.type === 'between' && (
                  <div className="space-y-2">
                    <input
                      type="date"
                      placeholder="Start date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gcopper text-sm disabled:bg-gray-100"
                      value={displayFilters[attr.AttributeName]?.startDate || ''}
                      onChange={(e) =>
                        handleFilterChange(attr.AttributeName, {
                          ...displayFilters[attr.AttributeName],
                          startDate: e.target.value,
                        })
                      }
                      disabled={isAttributeDisabled}
                    />
                    <input
                      type="date"
                      placeholder="End date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gcopper text-sm disabled:bg-gray-100"
                      value={displayFilters[attr.AttributeName]?.endDate || ''}
                      onChange={(e) =>
                        handleFilterChange(attr.AttributeName, {
                          ...displayFilters[attr.AttributeName],
                          endDate: e.target.value,
                        })
                      }
                      disabled={isAttributeDisabled}
                    />
                  </div>
                )}

                {!isAttributeDisabled && available[0]?.count > 0 && (
                  <p className="text-xs text-gray-500">
                    {available[0].count} {available[0].count === 1 ? 'item' : 'items'} available
                  </p>
                )}
                {isAttributeDisabled && (
                  <p className="text-xs text-gray-400 italic">No items match this date range</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AttributeFilters;